import React, { useEffect } from "react";
import StackKit from "../../kit/stack";
import BoxKit from "../../kit/box";
import TypoGraphyKit from "../../kit/typoGraphy";
import { useQuery } from "@tanstack/react-query";
import { fetchWalletBalance } from "@/api/user/home";
import { SxProps, Theme } from "@mui/material";
import { ClassNames } from "@emotion/react";
import { toPersianNumber } from "@/hooks/useThousandsSeparator";
type ImgStaticCardProps = {
  imgSlider1: string;
  imgSlider2: string;
  widthBox?: string;
  img1Width?: string;
  img1Height?: string;
  img2Width?: string;
  img2Height?: string;
  sx?: SxProps<Theme>;
  className?: string;
};
/**

ImgStaticCard

نمایش کارت با تصاویر ثابت و اطلاعات کارت واقعی از API.

این کامپوننت:

دو تصویر اسلایدر روی هم نمایش می‌دهد.

شماره کارت را به صورت چهار رقمی بلوک‌بندی شده نمایش می‌دهد.

اطلاعات CVV و تاریخ انقضا کارت را نمایش می‌دهد.

امکان تنظیم ابعاد تصاویر و جعبه کارت با props وجود دارد.

@param {string} imgSlider1 مسیر تصویر اول کارت
@param {string} imgSlider2 مسیر تصویر دوم کارت
@param {string} [widthBox='100%'] عرض کل جعبه کارت
@param {string} [img1Width='118px'] عرض تصویر اول کارت
@param {string} [img1Height='162px'] ارتفاع تصویر اول کارت
@param {string} [img2Width='98px'] عرض تصویر دوم کارت
@param {string} [img2Height='162px'] ارتفاع تصویر دوم کارت
@param {SxProps<Theme>} [sx] استایل‌های اضافی MUI برای Box
@param {string} [className] کلاس‌های CSS اضافی برای Stack
@returns JSX.Element کامپوننت کارت با اطلاعات و تصاویر ثابت
*/
const ImgStaticCard = (props: ImgStaticCardProps) => {
  const {
    imgSlider1,
    imgSlider2,
    widthBox = "100%",
    img1Width = "118px",
    img1Height = "162px",
    img2Width = "98px",
    img2Height = "162px",
    sx,
    className = "",
  } = props;
  const { data } = useQuery({
    queryKey: ["img-balance"],
    queryFn: fetchWalletBalance,
  });

  const cardData = data?.cards;
  const cardNumber = String(data?.cards?.[0]?.number || "");
  const cardChunks = cardNumber.match(/.{1,2}/g) || [];
  return (
    <StackKit
      direction={"row"}
      className={`${className}`}
      sx={{
        width: widthBox,
        height: "100%",
        position: "relative",
        alignItems: "center",
      }}
    >
      <BoxKit
        sx={{
          position: "absolute",
          right: "10px",
          width: img1Width,
          zIndex: 1,
          height: img1Height,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <img
          src={imgSlider1}
          alt="slid1"
          style={{ height: "100%", width: "100%", objectFit: "cover" }}
        />
        <BoxKit
          sx={{
            position: "absolute",
            top: 0,
            left: "-10px",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "end",
            flexDirection: "column",
            color: "white",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        >
          <StackKit
            sx={{ width: "100%", marginLeft: "20px" }}
            alignItems={"center"}
          >
            <StackKit
              direction="row"
              spacing={1}
              justifyContent="center"
              gap={1}
              alignItems={"start"}
              sx={{ direction: "ltr" }}
            >
              <TypoGraphyKit
                component={"span"}
                sx={{
                  fontSize: "9px",
                  fontWeight: "bold",
                  color: "#fff !important",
                }}
              >
                {toPersianNumber(cardChunks[0])}
              </TypoGraphyKit>
              <TypoGraphyKit
                component={"span"}
                sx={{
                  fontSize: "9px",
                  color: "#fff !important",
                  fontWeight: "bold",
                }}
              >
                {toPersianNumber(cardChunks[1])}
              </TypoGraphyKit>
            </StackKit>
            <StackKit
              direction="row"
              spacing={1}
              justifyContent="center"
              gap={1}
              alignItems={"start"}
              sx={{ direction: "ltr" }}
            >
              <TypoGraphyKit
                component={"span"}
                sx={{
                  fontSize: "9px",
                  color: "#fff !important",
                  fontWeight: "bold",
                }}
              >
                {toPersianNumber(cardChunks[2])}
              </TypoGraphyKit>
              <TypoGraphyKit
                component={"span"}
                sx={{
                  fontSize: "9px",
                  color: "#fff !important",
                  fontWeight: "bold",
                }}
              >
                {toPersianNumber(cardChunks[3])}
              </TypoGraphyKit>
            </StackKit>
            <StackKit
              direction="row"
              spacing={1}
              justifyContent="center"
              gap={1}
              alignItems={"start"}
              sx={{ direction: "ltr" }}
            >
              <TypoGraphyKit
                component={"span"}
                sx={{
                  fontSize: "9px",
                  color: "#fff !important",
                  fontWeight: "bold",
                }}
              >
                {toPersianNumber(cardChunks[4])}
              </TypoGraphyKit>
              <TypoGraphyKit
                component={"span"}
                sx={{
                  fontSize: "9px",
                  color: "#fff !important",
                  fontWeight: "bold",
                }}
              >
                {toPersianNumber(cardChunks[5])}
              </TypoGraphyKit>
            </StackKit>
            <StackKit
              direction={"row"}
              sx={{ width: "90%" }}
              justifyContent={"center"}
            >
              <StackKit direction={"row"} justifyContent={"start"} gap={2}>
                <StackKit
                  direction={"row-reverse"}
                  justifyContent={"start"}
                  sx={{
                    paddingLeft: "5px",

                    textAlign: "left",
                  }}
                  gap={1}
                >
                  <TypoGraphyKit
                    component={"span"}
                    variant="body1"
                    sx={{
                      fontSize: "9px",

                      color: "#fff !important",
                      fontWeight: "400",
                    }}
                  ></TypoGraphyKit>

                  <TypoGraphyKit
                    variant="body1"
                    sx={{
                      fontSize: "9px",
                      color: "#fff !important",
                      fontWeight: "400",
                    }}
                  >
                    {toPersianNumber(cardData && cardData[0]?.expire)}
                  </TypoGraphyKit>
                </StackKit>
                <StackKit
                  direction={"row"}
                  justifyContent={"end"}
                  sx={{ direction: "ltr" }}
                  alignItems={"center"}
                >
                  <TypoGraphyKit
                    variant="body1"
                    sx={{ fontSize: "9px", color: "#fff !important" }}
                  ></TypoGraphyKit>
                  <TypoGraphyKit
                    component={"span"}
                    variant="body1"
                    sx={{
                      fontSize: "9px",
                      color: "#fff !important",
                      fontWeight: "400",
                      direction: "ltr",
                    }}
                  >
                    {toPersianNumber(cardData && cardData[0]?.cvv)}
                  </TypoGraphyKit>
                </StackKit>
              </StackKit>
            </StackKit>
          </StackKit>
        </BoxKit>
      </BoxKit>
      <BoxKit
        sx={{
          position: "absolute",
          width: img2Width,
          height: img2Height,
          right: "100px",
        }}
      >
        <img
          src={imgSlider2}
          alt="slid2"
          style={{ height: "100%", borderRadius: "8px" }}
        />
      </BoxKit>
    </StackKit>
  );
};

export default ImgStaticCard;
